/**
 * "Cost" gadget (Operate): the person's AI and sandbox credits spent this month (`GET /api/v1/me/credits`, all their
 * projects: the gateway keeps credits per person, so it says "your") and an estimate of the app in production per
 * month, from vfs-service's FinOps model with the FinOps defaults the Flow admin set (Kubernetes, eu-west, 1,000
 * requests a day, no AI calls, until changed), from what each service uses now and the project's datastores: the same
 * figure as the FinOps tab opens on. The estimate is labelled one. Link: Operate › FinOps.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { creditsAmount } from '../../../utils/creditsModel';
import { money, shortCount } from '../../../utils/finopsFormat';
import { costSummary } from '../../../utils/homeGadgets/operate';
import GadgetCard, { TabLink } from './GadgetCard';
import { useFinOps, useMyCredits } from './sources';

export default function CostGadget({ projectId, onOpenFinOps = null }) {
  const credits = useMyCredits();
  const finops = useFinOps(projectId);
  const cost = costSummary({ me: credits.data, finops: finops.data });
  let spent;
  if (cost.spent !== null) { spent = creditsAmount(cost.spent); }
  else if (credits.loading && !credits.data) { spent = '…'; }
  else { spent = '—'; }
  const spentNote = cost.spent !== null ? null
    : credits.error && !credits.data ? 'Your credits could not be read just now.'
      : !credits.loading ? 'No credit spending to show for your plan.' : null;
  let estimateText;
  if (cost.estimate !== null) { estimateText = `≈ ${money(cost.estimate, { symbol: cost.symbol })}`; }
  else if (finops.loading && !finops.data) { estimateText = '…'; }
  else { estimateText = '—'; }
  let basis;
  if (cost.estimate !== null) {
    basis = (
      <p className="gd-note" data-testid="cost-basis">
        Estimate: {cost.runtime}, {cost.region}, {shortCount(cost.requestsPerDay)} requests a day, approximate list prices;
        {cost.measured < cost.services ? ` ${cost.services - cost.measured} of ${cost.services} services not running, sized by default.` : ' sized from what the services use now.'}
        {finops.data?.inputs?.storesUnavailable ? ' The datastores could not be read, so they are not counted.' : ''}
      </p>
    );
  } else if (finops.error && !finops.data) {
    basis = <p className="gd-note">The estimate could not be worked out just now.</p>;
  } else if (!finops.loading) {
    basis = <p className="gd-note">No service to estimate yet.</p>;
  }
  return (
    <GadgetCard links={<TabLink onClick={onOpenFinOps}>Operate › FinOps</TabLink>} testId="cost-gadget" title="Cost">
      <div className="gd-tiles">
        <div className="gd-tile" data-testid="cost-credits">
          <strong>{spent}</strong>
          <span>your credits this month, all projects</span>
        </div>
        <div className="gd-tile" data-testid="cost-estimate">
          <strong>{estimateText}</strong>
          <span>a month in production, an estimate</span>
        </div>
      </div>
      {spentNote && <p className="gd-note">{spentNote}</p>}
      {basis}
    </GadgetCard>
  );
}

CostGadget.propTypes = {
  onOpenFinOps: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
